import ApiClient from "@/common/http/ApiClient";

class ProfilesApi {

  // team profiles
  static search(params, allowMultiRequests = false) {
    return ApiClient.get("team_profiles", params, { allowMultiRequests }); // Searches recruiter app data
  }
  static recommendations(params, allowMultiRequests = false) {
    return ApiClient.get(`projects/${params.projectId}/recommendations`, { ...params, async: false }, { allowMultiRequests }); // Searches pensieve using similarity
  }
  static async asyncRecommendations(params) {
    const data = await ApiClient.get(`projects/${params.projectId}/recommendations`, { ...params, async: true }); // Searches pensieve using similarity
    return data;
  }
  static pollRecommendations(params) {
    const queryParams = { xid: params.xid };
    if (params.partialResults) {
      queryParams.partial_results = true;
      queryParams.sort_field = params.sortField;
    }
    return ApiClient.get(`projects/${params.projectId}/recommendations/poll`, queryParams, { allowMultiRequests: false });
  }
  static pipelineCounts(params) {
    return ApiClient.get("team_profiles/pipeline_counts", params);
  }
  static review(params) {
    return ApiClient.post("team_profile_reviews", params);
  }
  static undoReview(id) {
    return ApiClient.delete(`team_profile_reviews/${id}`);
  }
  static showProfile(params) {
    return ApiClient.get("team_profiles/profile", params);
  }
  static showRecommendation(params) {
    return ApiClient.get(`projects/${params.projectId}/recommendations/profile`, params);
  }
  static showProfileSummary(params) {
    return ApiClient.get("profile_summaries", params);
  }
  static showProfileApplications(params) {
    return ApiClient.get("team_profiles/profile_applications", params);
  }
  static atsReviewerStageActions(params) {
    return ApiClient.get("team_profiles/ats_reviewer_stage_actions", params);
  }
  // Batched: returns { actionsByJob: { <atsJobId>: [actions] } } for all of a profile's
  // non-archived applications in one request (avoids one request per job).
  static atsReviewerStageActionsForProfile(params) {
    // Silent background fetch of available actions — don't fire the global error toast for a failure
    // the user never initiated; the caller handles it quietly.
    return ApiClient.get("team_profiles/ats_reviewer_stage_actions_for_profile", params, { skipErrorNotification: true });
  }
  static applyAtsReviewerStageAction(params = {}) {
    // The jobs tab shows its own specific error message, so suppress the global toast to avoid a duplicate.
    return ApiClient.post("team_profiles/apply_ats_reviewer_stage_action", params, { skipErrorNotification: true });
  }
  static showProfileCompanies(params) {
    return ApiClient.get("team_profiles/profile_companies", params);
  }
  static showProfileCrm(params) {
    return ApiClient.get("team_profiles/profile_crm", params);
  }
  static showProfileOutreach(params) {
    return ApiClient.get("team_profiles/profile_outreach", params);
  }
  static tagOptions() {
    return ApiClient.get("team_profiles/tag_options");
  }
  static dedupeMessage(params) {
    if (params.profileId) {
      return ApiClient.get("team_profiles/dedupe_message", params);
    }
    return ApiClient.get(`projects/${params.projectId}/recommendations/dedupe_message`, params);
  }
  static download(params) {
    return ApiClient.put("team_profiles/download", params);
  }
  static liMessagingContent(params) {
    return ApiClient.get("team_profiles/li_messaging_content", params);
  }
  static markAsSentViaInMail(params) {
    return ApiClient.put("team_profiles/mark_as_sent_via_inmail", params);
  }
  static watchProfile(params) {
    return ApiClient.put("team_profiles/watch", params);
  }
  static updateProfileTags(params) {
    return ApiClient.put("team_profiles/update_tags", params);
  }
  static addToCampaign(params, useRecommendations) {
    if (useRecommendations) {
      return ApiClient.post(`projects/${params.recommendationsProjectId}/recommendations/add_to_campaign`, params);
    }
    return ApiClient.post("team_profiles/add_to_campaign", params);
  }
  static notes(params) {
    return ApiClient.get("team_profile_notes", params);
  }
  static createNote(params) {
    return ApiClient.post("team_profile_notes", params);
  }
  static deleteNote(id) {
    return ApiClient.delete(`team_profile_notes/${id}`);
  }
  static fetchAllForReview() {
    return ApiClient.get("team_profile_reviews");
  }
  static fetchReviewDataForProject(projectId) {
    return ApiClient.get(`team_profile_reviews/for_project/${projectId}`);
  }
  static fetchCountForReview() {
    return ApiClient.get("team_profile_reviews/count");
  }
  static createReminder(params) {
    return ApiClient.post("team_profile_reminders", params);
  }
  static deleteReminder(id) {
    return ApiClient.delete(`team_profile_reminders/${id}`);
  }
  static updateProfile(params) {
    return ApiClient.put("team_profiles/update_team_profile", params);
  }
  static deleteCandidate(params) {
    return ApiClient.put("team_profiles/delete_candidate", params);
  }
  static profileIntros(params) {
    return ApiClient.get("team_profiles/profile_intros", params);
  }
  static socialLinks(profileId) {
    return ApiClient.get("team_profiles/social_links", { profile_id: profileId });
  }
  static phonePresentCount(params) {
    return ApiClient.get("team_profiles/phone_present_count", params);
  }
  static pollTeamProfilePartialResults(params) {
    return ApiClient.get("team_profiles/poll_partial_results", params, { allowMultiRequests: false });
  }

  // INTRO
  static candidateConnectors(params) {
    return ApiClient.get("connectors", params);
  }
  static saveCandidateConnectors(params) {
    return ApiClient.post("intro_packs", params);
  }
  static addIntroToIntroPack(introPackId, params) {
    return ApiClient.post(`intro_packs/${introPackId}/intros`, params);
  }
  static cancelIntro(id, params) {
    return ApiClient.post(`intros/${id}/cancel`, params);
  }
  static cancelIntroPack(id, params) {
    return ApiClient.post(`intro_packs/${id}/cancel`, params);
  }
  static forceFallbackForIntroPacks(id, params) {
    return ApiClient.post(`intro_packs/${id}/fallback`, params);
  }
}

export default ProfilesApi;
